import React, { useEffect, useState, useCallback } from "react";
import AuthLayout from "../../../../layout/AuthLayout";
import { useDispatch } from "react-redux";
import { updateRoute } from "../../../../store/dataRoute";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  getDetailProduct,
  updateBulkProduct,
  deleteBulkProduct,
  updateBulkProductStatus,
} from "../../../../services/products";
import { getCategorysList } from "../../../../services/category";
import { getAllCountryList } from "../../../../services/location";
import { getAllWarehouseList } from "../../../../services/warehouse";
import {
  notificationSuccess,
  notificationError,
} from "../../../../component/ui/Notifications";
import { useLocalStorage } from "@mantine/hooks";

import BulkEditForm from "./BulkEditForm";
import SelectedProductsPreview from "./SelectedProductsPreview";

// ─── Tag definitions (mirrors SummaryLabelProduct) ───────────────────────────
export const TAG_LIST = [
  { value: "1", label: "Wholesale", caption: "Wholesale Product" },
  { value: "2", label: "Retail", caption: "Retail Product" },
  { value: "3", label: "Hot Product", caption: "Hot Product" },
  { value: "4", label: "Local", caption: "Local Product" },
  { value: "5", label: "Imported Product", caption: "Imported Product" },
  { value: "6", label: "Fulfillment", caption: "Fulfillment Product" },
  {
    value: "8",
    label: "Virtual Product",
    caption: "No domestic shipping cost",
  },
  { value: "9", label: "COD", caption: "COD Product" },
  { value: "10", label: "Pre-order", caption: "Pre-order Product" },
  {
    value: "11",
    label: "Hot Product (Home)",
    caption: "Hot Product for Homepage",
  },
];

// ─── Weight units ─────────────────────────────────────────────────────────────
export const WEIGHT_UNIT_OPTIONS = [
  { value: "gr", label: "Gram (g)" },
  { value: "kg", label: "Kilogram (kg)" },
  { value: "lb", label: "Pound (lb)" },
  { value: "oz", label: "Ounce (oz)" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const extractProductMeta = (res) => {
  const attributes = res.data?.attributes || {};
  const relationships = res.data?.relationships || {};
  const included = res.included || {};
  const tags = included.tags || {};
  const categories = included.categories || {};
  const warehouses = included.warehouses || {};

  return {
    id: attributes.id,
    title: attributes.title,
    image: attributes.image,
    currentCategories: (relationships.categories || [])
      .filter((c) => categories[c.id]?.attributes?.level === 3)
      .map((c) => ({
        id: String(c.id),
        name: categories[c.id]?.attributes?.name || `Cat #${c.id}`,
      })),
    currentTags: Object.keys(tags).map((k) => String(k)),
    currentWeight: attributes.weight || 0,
    currentWeightUnit: attributes.weight_unit || "gr",
    currentCountryId: attributes.country_id
      ? String(attributes.country_id)
      : null,
    currentWarehouses: (relationships.warehouses || []).map((w) => ({
      id: String(w.id),
      name: warehouses[w.id]?.attributes?.name || `Warehouse #${w.id}`,
    })),
  };
};

const BulkEditProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Persisted bulk selection ──────────────────────────────────────────────
  const [, setSelectedBulkIds] = useLocalStorage({
    key: "bulk-edit-product-ids",
    defaultValue: [],
  });

  // ── Products fetched for preview ──────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // ── Reference data ────────────────────────────────────────────────────────
  const [categoryLvl1, setCategoryLvl1] = useState([]);
  const [categoryLvl2, setCategoryLvl2] = useState([]);
  const [categoryLvl3, setCategoryLvl3] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  // ── Bulk edit form state ──────────────────────────────────────────────────
  const [selCatLvl1, setSelCatLvl1] = useState(null);
  const [selCatLvl2, setSelCatLvl2] = useState(null);
  const [selCatLvl3, setSelCatLvl3] = useState(null);
  const [selTags, setSelTags] = useState([]);
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("gr");
  const [countryId, setCountryId] = useState(null);
  const [selWarehouse, setSelWarehouse] = useState(null);
  const [sellerStatus, setSellerStatus] = useState(null);
  const [visibilityValue, setVisibilityValue] = useState(null);

  // ── Operation state ───────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [lastChanges, setLastChanges] = useState(null);

  // ── Breadcrumb ────────────────────────────────────────────────────────────
  const mappingRoute = [
    { label: "/", route: null },
    { label: "Product", route: "/product/all" },
    { label: "Bulk Edit", route: null },
  ];

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(updateRoute({ data: mappingRoute }));
    fetchProducts();
    fetchReferenceData();
    // eslint-disable-next-line
  }, [dispatch]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    const idsString = searchParams.get("ids");
    if (!idsString) {
      setProducts([]);
      setLoadingProducts(false);
      return;
    }
    const ids = idsString
      .split(",")
      .map((id) => Number(id))
      .filter(Boolean);
    if (ids.length === 0) {
      setProducts([]);
      setLoadingProducts(false);
      return;
    }
    try {
      const results = await Promise.all(
        ids.map((id) =>
          getDetailProduct(id, {
            include: "categories,warehouses,tags,country",
          }),
        ),
      );
      const mapped = results
        .filter((r) => r?.success)
        .map((r) => extractProductMeta(r));
      setProducts(mapped);
    } catch (err) {
      console.error("fetchProducts error:", err);
      notificationError("Error", "Failed to load selected products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchReferenceData = async () => {
    setLoadingRefs(true);
    try {
      const [catRes, countryRes, warehouseRes] = await Promise.all([
        getCategorysList({
          "page[num]": 1,
          "page[limit]": 100,
          "filter[level][is]": 1,
          "fields[category]": "name",
        }),
        getAllCountryList({
          "page[num]": 1,
          "page[limit]": 300,
          "fields[country]": "name",
        }),
        getAllWarehouseList({
          "page[num]": 1,
          "page[limit]": 100,
          sort: "-created_at",
        }),
      ]);

      const cats = (catRes?.data?.data || []).map((v) => ({
        value: String(v.attributes.id),
        label: v.attributes.name,
      }));
      setCategoryLvl1(cats);

      const countries = (countryRes?.data || []).map((v) => ({
        value: String(v.attributes.id),
        label: v.attributes.name,
      }));
      setCountryList(countries);

      const warehouses = (warehouseRes?.data || []).map((v) => ({
        value: String(v.attributes.id),
        label: v.attributes.name,
      }));
      setWarehouseList(warehouses);
    } catch (err) {
      console.error("fetchReferenceData error:", err);
    } finally {
      setLoadingRefs(false);
    }
  };

  const loadCategoryLvl2 = useCallback(async (parentId) => {
    if (!parentId) {
      setCategoryLvl2([]);
      setSelCatLvl2(null);
      setCategoryLvl3([]);
      setSelCatLvl3(null);
      return;
    }
    try {
      const res = await getCategorysList({
        "page[num]": 1,
        "page[limit]": 100,
        "filter[parent_id][is]": parentId,
        "fields[category]": "name",
      });
      const cats = (res?.data?.data || []).map((v) => ({
        value: String(v.attributes.id),
        label: v.attributes.name,
      }));
      setCategoryLvl2(cats);
      setSelCatLvl2(null);
      setCategoryLvl3([]);
      setSelCatLvl3(null);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadCategoryLvl3 = useCallback(async (parentId) => {
    if (!parentId) {
      setCategoryLvl3([]);
      setSelCatLvl3(null);
      return;
    }
    try {
      const res = await getCategorysList({
        "page[num]": 1,
        "page[limit]": 100,
        "filter[parent_id][is]": parentId,
        "fields[category]": "name",
      });
      const cats = (res?.data?.data || []).map((v) => ({
        value: String(v.attributes.id),
        label: v.attributes.name,
      }));
      setCategoryLvl3(cats);
      setSelCatLvl3(null);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleRemoveProduct = (id) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setSelectedBulkIds((prev) => prev.filter((item) => item !== id));
  };

  const buildCategories = () => {
    const cats = [];
    if (selCatLvl1) cats.push(Number(selCatLvl1));
    if (selCatLvl2) cats.push(Number(selCatLvl2));
    if (selCatLvl3) cats.push(Number(selCatLvl3));
    return cats;
  };

  const buildPayload = () => {
    const cats = buildCategories();
    const tags = selTags.map(Number);
    const hasWeight = weight !== "" && weight !== null;

    return {
      data: products.map((product) => {
        const attributes = {};
        const relationships = [];

        if (hasWeight) {
          attributes.weight = String(weight);
          attributes.weight_unit = weightUnit;
        }

        if (countryId) attributes.country_id = Number(countryId);

        if (cats.length > 0) {
          relationships.push({
            id: cats[cats.length - 1],
            type: "categories",
          });
        }

        if (tags.length > 0) {
          relationships.push({
            id: tags,
            type: "tags",
          });
        }

        if (selWarehouse) {
          relationships.push({
            id: [Number(selWarehouse)],
            type: "warehouses",
          });
        }

        return {
          id: product.id,
          ...(attributes &&
            Object.keys(attributes).length > 0 && { attributes }),
          relationships,
        };
      }),
    };
  };

  const hasPayloadChanges = (payload) =>
    payload.data.some(
      (item) =>
        Object.keys(item.attributes || {}).length > 0 ||
        (item.relationships && Object.keys(item.relationships).length > 0),
    );

  const handleSave = async () => {
    if (products.length === 0) return;
    const mainPayload = buildPayload();
    const hasMainChanges = hasPayloadChanges(mainPayload);
    const hasStatusChanges = sellerStatus !== null || visibilityValue !== null;

    if (!hasMainChanges && !hasStatusChanges) {
      notificationError(
        "No Changes",
        "Please select at least one field to update.",
      );
      return;
    }
    setSaving(true);
    try {
      const promises = [];

      if (hasMainChanges) {
        promises.push(
          updateBulkProduct(mainPayload).then((res) => {
            if (res?.success === false) {
              throw new Error(res?.message || "Could not update products.");
            }
          })
        );
      }

      if (hasStatusChanges) {
        const statusPayload = {
          ids: products.map((p) => p.id),
          ...(sellerStatus && { status: sellerStatus }),
          ...(visibilityValue && {
            visibility: visibilityValue === "live",
            not_show_in_catalog: visibilityValue === "hidden",
          }),
        };
        promises.push(
          updateBulkProductStatus(statusPayload).then((res) => {
            if (res?.success === false) {
              throw new Error(res?.message || "Could not update status/visibility.");
            }
          })
        );
      }

      await Promise.all(promises);

      notificationSuccess(
        "Bulk Update Successful",
        `Updated ${products.length} products.`,
      );
      setSelectedBulkIds([]);

      const categoriesApplied = [];
      if (selCatLvl1)
        categoriesApplied.push(
          categoryLvl1.find((c) => c.value === selCatLvl1)?.label,
        );
      if (selCatLvl2)
        categoriesApplied.push(
          categoryLvl2.find((c) => c.value === selCatLvl2)?.label,
        );
      if (selCatLvl3)
        categoriesApplied.push(
          categoryLvl3.find((c) => c.value === selCatLvl3)?.label,
        );

      setLastChanges({
        timestamp: new Date().toLocaleTimeString(),
        count: products.length,
        categories:
          categoriesApplied.length > 0 ? categoriesApplied.join(" > ") : null,
        tags:
          selTags.length > 0
            ? selTags
                .map((t) => TAG_LIST.find((tl) => tl.value === t)?.label || t)
                .join(", ")
            : null,
        weight:
          weight !== "" && weight !== null ? `${weight} ${weightUnit}` : null,
        country: countryId
          ? countryList.find((c) => c.value === countryId)?.label
          : null,
        warehouse: selWarehouse
          ? warehouseList.find((w) => w.value === selWarehouse)?.label
          : null,
        sellerStatus: sellerStatus,
        visibility: visibilityValue ? (visibilityValue === "live" ? "Live" : "Hidden from Catalog") : null,
      });

      setProducts((prevProducts) => {
        return prevProducts.map((p) => {
          const updatedProduct = { ...p };
          if (weight !== "" && weight !== null) {
            updatedProduct.currentWeight = Number(weight);
            updatedProduct.currentWeightUnit = weightUnit;
          }
          if (countryId) {
            updatedProduct.currentCountryId = countryId;
          }
          if (selTags.length > 0) {
            updatedProduct.currentTags = selTags;
          }
          if (selWarehouse) {
            const matchedWh = warehouseList.find(
              (w) => w.value === selWarehouse,
            );
            updatedProduct.currentWarehouses = [
              {
                id: selWarehouse,
                name: matchedWh
                  ? matchedWh.label
                  : `Warehouse #${selWarehouse}`,
              },
            ];
          }
          if (selCatLvl1 || selCatLvl2 || selCatLvl3) {
            let catName = "";
            if (selCatLvl3) {
              const found = categoryLvl3.find((c) => c.value === selCatLvl3);
              catName = found ? found.label : `Cat #${selCatLvl3}`;
            } else if (selCatLvl2) {
              const found = categoryLvl2.find((c) => c.value === selCatLvl2);
              catName = found ? found.label : `Cat #${selCatLvl2}`;
            } else if (selCatLvl1) {
              const found = categoryLvl1.find((c) => c.value === selCatLvl1);
              catName = found ? found.label : `Cat #${selCatLvl1}`;
            }
            const catId = selCatLvl3 || selCatLvl2 || selCatLvl1;
            updatedProduct.currentCategories = [
              {
                id: String(catId),
                name: catName,
              },
            ];
          }
          return updatedProduct;
        });
      });

      setSelCatLvl1(null);
      setSelCatLvl2(null);
      setSelCatLvl3(null);
      setSelTags([]);
      setWeight("");
      setCountryId(null);
      setSelWarehouse(null);
      setSellerStatus(null);
      setVisibilityValue(null);
    } catch (err) {
      console.error("handleSave error:", err);
      notificationError("Error", err.message || "An error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (products.length === 0) return;
    setDeleting(true);
    try {
      const res = await deleteBulkProduct({ ids: products.map((p) => p.id) });
      if (res?.success !== false) {
        notificationSuccess(
          "Bulk Delete Successful",
          `Deleted ${products.length} products.`,
        );
        setSelectedBulkIds([]);
        closeDeleteModal();
        navigate("/product/all");
      } else {
        notificationError(
          "Bulk Delete Failed",
          res?.message || "Could not delete products.",
        );
        closeDeleteModal();
      }
    } catch (err) {
      console.error("handleDelete error:", err);
      notificationError("Error", "An error occurred while deleting.");
      closeDeleteModal();
    } finally {
      setDeleting(false);
    }
  };

  const isLoading = loadingProducts || loadingRefs;

  return (
    <AuthLayout>
      <Box my={20} pos="relative">
        <LoadingOverlay
          visible={saving || deleting}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
        />

        {/* Header */}
        <Flex justify="space-between" align="center" mb={24}>
          <Box>
            <Title order={2}>Bulk Edit Products</Title>
            <Text size="sm" c="dimmed">
              Edit category, tags, weight, country, and warehouse for multiple
              products at once
            </Text>
          </Box>
          <Group>
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              disabled={products.length === 0 || isLoading}
              onClick={openDeleteModal}
            >
              Delete All ({products.length})
            </Button>
            <Button
              variant="outline"
              color="gray"
              leftSection={<IconX size={16} />}
              onClick={() => navigate("/product/all")}
            >
              Cancel
            </Button>
            {lastChanges && (
              <Button
                variant="outline"
                color="red"
                onClick={() => navigate("/product/all")}
              >
                Finish
              </Button>
            )}
          </Group>
        </Flex>

        {/* Info of the user / What already changed to the server */}
        {lastChanges && (
          <Paper
            withBorder
            p="md"
            mb={20}
            radius="md"
            style={{
              borderColor: "var(--mantine-color-green-filled)",
              backgroundColor: "rgba(76, 201, 240, 0.05)",
            }}
          >
            <Flex justify="space-between" align="center" mb={8}>
              <Text fw={600} size="sm" c="green">
                ✓ Bulk Update Successfully Applied to {lastChanges.count}{" "}
                Product{lastChanges.count !== 1 ? "s" : ""} (at{" "}
                {lastChanges.timestamp})
              </Text>
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => setLastChanges(null)}
              >
                Dismiss
              </Button>
            </Flex>
            <Text size="xs" c="dimmed" mb={4}>
              The following changes were synchronized with the server:
            </Text>
            <Grid gutter="xs">
              {lastChanges.categories && (
                <Grid.Col span={4}>
                  <Text size="xs" fw={500}>
                    Category:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {lastChanges.categories}
                  </Text>
                </Grid.Col>
              )}
              {lastChanges.tags && (
                <Grid.Col span={4}>
                  <Text size="xs" fw={500}>
                    Tags/Labels:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {lastChanges.tags}
                  </Text>
                </Grid.Col>
              )}
              {lastChanges.weight && (
                <Grid.Col span={4}>
                  <Text size="xs" fw={500}>
                    Weight:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {lastChanges.weight}
                  </Text>
                </Grid.Col>
              )}
              {lastChanges.country && (
                <Grid.Col span={4}>
                  <Text size="xs" fw={500}>
                    Country:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {lastChanges.country}
                  </Text>
                </Grid.Col>
              )}
              {lastChanges.warehouse && (
                <Grid.Col span={4}>
                  <Text size="xs" fw={500}>
                    Warehouse:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {lastChanges.warehouse}
                  </Text>
                </Grid.Col>
              )}
              {lastChanges.sellerStatus && (
                <Grid.Col span={4}>
                  <Text size="xs" fw={500}>
                    Seller Status:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {lastChanges.sellerStatus}
                  </Text>
                </Grid.Col>
              )}
              {lastChanges.visibility && (
                <Grid.Col span={4}>
                  <Text size="xs" fw={500}>
                    Visibility:
                  </Text>
                  <Text size="xs" c="dimmed">
                    {lastChanges.visibility}
                  </Text>
                </Grid.Col>
              )}
            </Grid>
          </Paper>
        )}

        {/* Empty state */}
        {!isLoading && products.length === 0 ? (
          <Paper withBorder p={40} radius="md" ta="center">
            <Text fw={500} mb={10}>
              No products selected for bulk editing
            </Text>
            <Text size="sm" c="dimmed" mb={20}>
              Go back to the product list and add products to the bulk edit list
              first.
            </Text>
            <Button onClick={() => navigate("/product/all")}>
              Back to Product List
            </Button>
          </Paper>
        ) : (
          <Grid gutter="md">
            {/* ── Left: Edit Form ─────────────────────────────────────────── */}
            <Grid.Col span={{ base: 12, md: 5 }}>
              <BulkEditForm
                categoryLvl1={categoryLvl1}
                categoryLvl2={categoryLvl2}
                categoryLvl3={categoryLvl3}
                selCatLvl1={selCatLvl1}
                setSelCatLvl1={setSelCatLvl1}
                selCatLvl2={selCatLvl2}
                setSelCatLvl2={setSelCatLvl2}
                selCatLvl3={selCatLvl3}
                setSelCatLvl3={setSelCatLvl3}
                loadCategoryLvl2={loadCategoryLvl2}
                loadCategoryLvl3={loadCategoryLvl3}
                loadingRefs={loadingRefs}
                selTags={selTags}
                setSelTags={setSelTags}
                tagList={TAG_LIST}
                weight={weight}
                setWeight={setWeight}
                weightUnit={weightUnit}
                setWeightUnit={setWeightUnit}
                countryList={countryList}
                countryId={countryId}
                setCountryId={setCountryId}
                warehouseList={warehouseList}
                selWarehouse={selWarehouse}
                setSelWarehouse={setSelWarehouse}
                sellerStatus={sellerStatus}
                setSellerStatus={setSellerStatus}
                visibilityValue={visibilityValue}
                setVisibilityValue={setVisibilityValue}
                saving={saving}
                productsLength={products.length}
                isLoading={isLoading}
                handleSave={handleSave}
              />
            </Grid.Col>

            {/* ── Right: Product Selection Preview ────────────────────────── */}
            <Grid.Col span={{ base: 12, md: 7 }}>
              <SelectedProductsPreview
                products={products}
                isLoading={isLoading}
                tagList={TAG_LIST}
                countryList={countryList}
                handleRemoveProduct={handleRemoveProduct}
                deleteModalOpened={deleteModalOpened}
                closeDeleteModal={closeDeleteModal}
                deleting={deleting}
                handleDelete={handleDelete}
              />
            </Grid.Col>
          </Grid>
        )}
      </Box>
    </AuthLayout>
  );
};

export default BulkEditProduct;
